import {
  type Change,
  type CreateChanges,
  type IncrementalChange,
  isIncrementalChange,
  isSetCreateChange,
  isSetItemUpdateChange,
  isValueChange,
  type SetCreateChange,
  type SetItemUpdateChange,
  type UpdateChanges,
} from '@openx/types/auditTrail';
import omit from 'lodash/omit';

export function normalizeSetCreateChange(setCreateChange: SetCreateChange): Change {
  return {
    added: Object.keys(setCreateChange),
  };
}

const sanitizeWhitelistRules = (whitelist_rules = {}) => {
  const keysToRemove: string[] = [];

  Object.keys(whitelist_rules).forEach(val => {
    if (typeof whitelist_rules[val] !== 'boolean') {
      keysToRemove.push(val);
    }
  });

  return omit(whitelist_rules, keysToRemove);
};

export function extractNormalizedCreateChange(fieldName: string, changes: CreateChanges): Change {
  let createChangeForField = changes.new_value[fieldName];

  if (fieldName === 'whitelist_rules') {
    createChangeForField = sanitizeWhitelistRules(changes.new_value[fieldName]);
  }

  if (isSetCreateChange(createChangeForField)) {
    return normalizeSetCreateChange(createChangeForField);
  }

  if (Array.isArray(createChangeForField)) {
    return {
      added: createChangeForField,
    };
  }

  return {
    new_value: typeof createChangeForField === 'boolean' ? String(createChangeForField) : createChangeForField,
  };
}

const SET_ITEM_UPDATE_KEY_SEPARATOR = '.';

export function extractSetItemsChanges(fieldName: string, changes: UpdateChanges): [string, SetItemUpdateChange][] {
  const expectedSetItemChangeKeyBeginning = fieldName.concat(SET_ITEM_UPDATE_KEY_SEPARATOR);

  return Object.entries(changes)
    .filter(
      (entry): entry is [string, SetItemUpdateChange] =>
        entry[0].startsWith(expectedSetItemChangeKeyBeginning) && isSetItemUpdateChange(entry[1])
    )
    .map(([key, change]) => {
      const [, itemValue] = key.split(expectedSetItemChangeKeyBeginning);

      return [itemValue, change];
    });
}

export function mapSetItemChangesToIncrementalChange(
  setItemChanges: [string, SetItemUpdateChange][]
): IncrementalChange {
  const added: string[] = [];
  const removed: string[] = [];

  setItemChanges.forEach(([itemValue, setItemChange]) => {
    if (setItemChange.new_value && !setItemChange.old_value) {
      added.push(itemValue);
    }

    if (setItemChange.old_value && !setItemChange.new_value) {
      removed.push(itemValue);
    }
  });

  return { added, removed };
}

export function extractNormalizedUpdateChange(fieldName: string, changes: UpdateChanges): Change | undefined {
  let updateChangeForField = changes[fieldName];

  if (fieldName === 'whitelist_rules') {
    updateChangeForField = sanitizeWhitelistRules(changes[fieldName]);
  }

  if (
    (isValueChange<string>(updateChangeForField) || isIncrementalChange<string>(updateChangeForField)) &&
    !isSetItemUpdateChange(updateChangeForField)
  ) {
    return updateChangeForField;
  }

  const setItemUpdateChanges = extractSetItemsChanges(fieldName, changes);

  if (setItemUpdateChanges.length) {
    return mapSetItemChangesToIncrementalChange(setItemUpdateChanges);
  }

  if (isValueChange<string>(updateChangeForField)) {
    const { new_value, old_value } = updateChangeForField;

    return {
      new_value: typeof new_value === 'boolean' ? String(new_value) : new_value,
      old_value: typeof old_value === 'boolean' ? String(old_value) : old_value,
    };
  }

  return undefined;
}
